import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiX, FiList, FiCalendar, FiCheckCircle, FiCircle, 
  FiPlus, FiClock, FiFlag, FiTrash2, FiChevronUp, FiChevronDown, FiCheck,
  FiAlertTriangle, FiShield, FiEye, FiEyeOff
} = FiIcons;

const ProjectManagementModal = ({ isOpen, onClose }) => {
  const [tasks, setTasks] = useState([
    { id: 1, title: 'Finalize Guest List', date: '2024-04-10', completed: true, priority: 'High' },
    { id: 2, title: 'Book Catering Services', date: '2024-04-12', completed: false, priority: 'High' },
    { id: 3, title: 'Legal: File Death Certificate', date: '2024-04-05', completed: true, priority: 'Estate Critical' },
    { id: 4, title: 'Estate: Transfer Digital Vault', date: '2024-04-15', completed: false, priority: 'Estate Critical' },
    { id: 5, title: 'Order Memorial Souvenirs', date: '2024-04-15', completed: false, priority: 'Medium' }
  ]);

  const [isAdding, setIsAdding] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', date: '', priority: 'Medium' });

  const toggleTask = (id) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(t => t.id !== id));
  };

  const addTask = (e) => {
    e.preventDefault();
    if (!newTask.title || !newTask.date) return;
    
    const task = {
      id: Date.now(),
      ...newTask,
      completed: false
    };
    
    setTasks([...tasks, task]);
    setNewTask({ title: '', date: '', priority: 'Medium' });
    setIsAdding(false);
  };

  const moveTask = (index, direction) => {
    const newTasks = [...tasks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= tasks.length) return;
    
    const temp = newTasks[index];
    newTasks[index] = newTasks[targetIndex];
    newTasks[targetIndex] = temp;
    setTasks(newTasks);
  };

  const handleCompletePlan = () => {
    setIsCompleted(true);
    setTimeout(() => {
      setIsCompleted(false);
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  // Logic: "Automatically unhide them in the task list view"
  // Even if hideCompleted is true, 'Estate Critical' tasks remain visible.
  const visibleTasks = tasks.filter(task => {
    if (!hideCompleted) return true;
    if (task.priority === 'Estate Critical') return true;
    return !task.completed;
  });

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'Estate Critical':
        return 'bg-red-600 text-white shadow-sm ring-2 ring-red-100';
      case 'High':
        return 'bg-red-50 text-red-500';
      case 'Medium':
        return 'bg-amber-50 text-amber-600';
      case 'Low':
        return 'bg-blue-50 text-blue-500';
      default:
        return 'bg-brand-light text-text-muted';
    }
  };

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-3xl overflow-hidden border border-brand-dark flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="p-8 md:p-12 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-white rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiList} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Project Management</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Memorial Dateline & Tasks</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto p-8 md:p-12 custom-scrollbar">
          {/* Timeline View */}
          <div className="mb-12">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-8 border-b border-brand-light pb-2">Execution Timeline</h4>
            <div className="relative flex justify-start items-start pt-4 px-4 overflow-x-auto pb-4 gap-8 min-h-[120px]">
              <div className="absolute top-8 left-0 right-0 h-0.5 bg-brand-dark opacity-30" />
              {tasks.map((task) => (
                <div key={task.id} className="relative z-10 flex flex-col items-center min-w-[140px]">
                  <motion.div 
                    layoutId={`dot-${task.id}`}
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-4 border-white shadow-md transition-colors ${task.completed ? 'bg-green-500' : task.priority === 'Estate Critical' ? 'bg-red-600' : 'bg-accent-gold'}`}
                  >
                    <SafeIcon icon={task.completed ? FiCheckCircle : task.priority === 'Estate Critical' ? FiShield : FiClock} className="w-4 h-4 text-white" />
                  </motion.div>
                  <p className={`text-[9px] font-black mt-4 uppercase text-center max-w-[100px] leading-tight ${task.completed ? 'text-text-muted opacity-60' : 'text-accent-navy'}`}>
                    {task.title}
                  </p>
                  <p className="text-[8px] text-text-muted mt-1 uppercase font-bold">
                    {task.date ? new Date(task.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'TBD'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Add Task Form */}
          <AnimatePresence>
            {isAdding && (
              <motion.form 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={addTask}
                className="mb-8 p-6 bg-brand-light/50 border-2 border-accent-gold/30 rounded-[32px] space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    autoFocus
                    type="text" 
                    placeholder="Task name (e.g. Legal: File Will)..." 
                    className="w-full px-4 py-3 bg-white border border-brand-dark rounded-xl text-sm outline-none focus:border-accent-gold shadow-sm"
                    value={newTask.title}
                    onChange={e => setNewTask({...newTask, title: e.target.value})}
                  />
                  <input 
                    type="date" 
                    className="w-full px-4 py-3 bg-white border border-brand-dark rounded-xl text-sm outline-none focus:border-accent-gold shadow-sm"
                    value={newTask.date}
                    onChange={e => setNewTask({...newTask, date: e.target.value})}
                  />
                </div>
                <div className="flex flex-wrap justify-between items-center gap-4">
                  <div className="flex flex-wrap gap-2">
                    {['Estate Critical', 'High', 'Medium', 'Low'].map(p => (
                      <button 
                        key={p}
                        type="button"
                        onClick={() => setNewTask({...newTask, priority: p})}
                        className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all ${newTask.priority === p ? (p === 'Estate Critical' ? 'bg-red-600 text-white' : 'bg-accent-navy text-white') : 'bg-white border border-brand-dark text-text-muted hover:border-accent-gold'}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setIsAdding(false)} className="px-4 py-2 text-[10px] font-black uppercase text-text-muted">Cancel</button>
                    <button type="submit" className="px-6 py-2 bg-accent-gold text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-accent-gold/20">Save Task</button>
                  </div>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Task List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Active Registry</h4>
                <button 
                  onClick={() => setHideCompleted(!hideCompleted)}
                  className={`flex items-center gap-2 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${hideCompleted ? 'bg-accent-navy text-white' : 'bg-brand-light text-text-muted border border-brand-dark'}`}
                >
                  <SafeIcon icon={hideCompleted ? FiEye : FiEyeOff} />
                  {hideCompleted ? 'Focus Mode On' : 'Hide Completed'}
                </button>
              </div>
              <button 
                onClick={() => setIsAdding(true)}
                className="text-[10px] font-black uppercase text-accent-gold hover:text-accent-navy flex items-center gap-2 transition-colors"
              >
                <SafeIcon icon={FiPlus} /> Add Task
              </button>
            </div>
            
            <div className="space-y-3">
              {visibleTasks.length === 0 && (
                <div className="text-center py-12 border-2 border-dashed border-brand-dark rounded-[32px]">
                  <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-brand-dark mx-auto mb-4" />
                  <p className="text-sm text-text-muted font-light">All active tasks completed. Switch off Focus Mode to see others.</p>
                </div>
              )}
              {visibleTasks.map((task, index) => (
                <motion.div 
                  layout
                  key={task.id} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`group p-5 rounded-[32px] border-2 transition-all flex items-center gap-6 ${task.completed ? 'bg-brand-light border-brand-dark opacity-60' : 'bg-white border-brand-dark hover:border-accent-gold shadow-sm'}`}
                >
                  <button onClick={() => toggleTask(task.id)} className="shrink-0 transition-transform active:scale-90">
                    <SafeIcon icon={task.completed ? FiCheckCircle : FiCircle} className={`w-7 h-7 ${task.completed ? 'text-green-500' : 'text-text-muted group-hover:text-accent-gold'}`} />
                  </button>
                  
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-1">
                      <h5 className={`text-sm font-bold transition-all ${task.completed ? 'text-text-muted line-through' : 'text-accent-navy'}`}>
                        {task.title}
                      </h5>
                      <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${getPriorityStyles(task.priority)}`}>
                        {task.priority === 'Estate Critical' && <SafeIcon icon={FiShield} className="w-2.5 h-2.5" />}
                        {task.priority}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-[10px] text-text-muted font-medium">
                      <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} /> {task.date}</span>
                      {task.priority === 'Estate Critical' && task.completed && (
                        <span className="text-red-600 font-black uppercase text-[8px] flex items-center gap-1">
                          <SafeIcon icon={FiAlertTriangle} /> Automatically Unhidden for Compliance
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex flex-col">
                      <button onClick={() => moveTask(index, 'up')} className="p-1 hover:text-accent-gold disabled:opacity-20" disabled={index === 0}>
                        <SafeIcon icon={FiChevronUp} />
                      </button>
                      <button onClick={() => moveTask(index, 'down')} className="p-1 hover:text-accent-gold disabled:opacity-20" disabled={index === visibleTasks.length - 1}>
                        <SafeIcon icon={FiChevronDown} />
                      </button>
                    </div>
                    <button onClick={() => deleteTask(task.id)} className="p-3 text-text-muted hover:text-red-500 transition-colors">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 bg-brand-light border-t border-brand-dark flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0">
          <div className="flex flex-col">
            <p className="text-[10px] font-black text-text-muted uppercase tracking-widest">
              {tasks.filter(t => t.completed).length} of {tasks.length} tasks completed
            </p>
            {tasks.some(t => t.priority === 'Estate Critical' && !t.completed) && (
              <p className="text-[9px] font-bold text-red-600 uppercase tracking-tight mt-1 animate-pulse">
                Pending Estate Critical Tasks Detected
              </p>
            )}
          </div>
          <button 
            onClick={handleCompletePlan}
            disabled={isCompleted}
            className={`w-full sm:w-auto px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${isCompleted ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90 shadow-xl shadow-accent-navy/20'}`}
          >
            {isCompleted ? (
              <><SafeIcon icon={FiCheck} /> Plan Archived</>
            ) : (
              'Complete & Lock Plan'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ProjectManagementModal;